import { ConstructionTypeIcon } from 'components/ConstructionTypeIcon';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { Loader } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import Styles from '../catalogConstructionTypes.module.css';
import { getCatalogConstructionTypeItemsGql } from '../catalogConstructionTypesQueries';
import { CatalogConstructionTypesRowMenu } from '../CatalogConstructionTypesRowMenu';
import type { TCatalogConstructionTypeRow } from '../types';

interface ICatalogConstructionTypesColumns {
    createOrEditModalHandler: (state: boolean) => void;
    setDataEditModal: Dispatch<SetStateAction<TCatalogConstructionTypeRow | null>>;
    onRemoveSuccess: () => void;
    setFilterValues: Dispatch<SetStateAction<Record<string, string[]>>>;
    catalogsRules: IUseAccess['catalogs'];
    catalogUniqueId?: string;
}

export const useCatalogConstructionTypesColumns = ({
    createOrEditModalHandler,
    setDataEditModal,
    onRemoveSuccess,
    setFilterValues,
    catalogsRules,
    catalogUniqueId,
}: ICatalogConstructionTypesColumns) => {
    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue: any) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    const columns = useMemo<ColDef<TCatalogConstructionTypeRow>[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 48,
                minWidth: 48,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                sortable: false,
                filter: false,
                cellRenderer: ({ data }: { data: TCatalogConstructionTypeRow }) =>
                    !data ? (
                        <Loader size="s" />
                    ) : (
                        <CatalogConstructionTypesRowMenu
                            createOrEditModalHandler={createOrEditModalHandler}
                            data={data}
                            setDataEditModal={setDataEditModal}
                            onRemoveSuccess={onRemoveSuccess}
                            catalogsRules={catalogsRules}
                        />
                    ),
            },
            {
                headerName: 'Наименование видов строительства',
                field: 'name',
                minWidth: 150,
                flex: 1,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogConstructionTypeItems',
                    customRequestId: 'catalogConstructionTypeNameFilter',
                    whereVariables: ['name'],
                    childFieldAccessor: 'name',
                    variables: catalogUniqueId ? { catalogUniqueId } : undefined,
                    columns: [
                        {
                            headerName: 'Наименование видов строительства',
                            field: 'name',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getCatalogConstructionTypeItemsGql,
                },
            },
            {
                headerName: 'Иконка',
                field: 'iconCode',
                width: 120,
                minWidth: 100,
                sortable: false,
                filter: false,
                cellRenderer: ({ data }: { data?: TCatalogConstructionTypeRow }) => (
                    <div className={Styles.iconCell}>
                        <ConstructionTypeIcon iconCode={data?.iconCode} />
                    </div>
                ),
            },
        ],
        [
            catalogUniqueId,
            catalogsRules,
            changeFilterHandler,
            createOrEditModalHandler,
            onRemoveSuccess,
            setDataEditModal,
        ],
    );

    const [selection, , , setSelection] = useSelection('catalogConstructionTypes');
    const [pinnedItems, , , setPinned] = usePin('catalogConstructionTypes');

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column) => {
                const newColumn = { ...column } as ColDef & { disabled: boolean };

                if (newColumn.field === 'menu') {
                    return acc;
                }

                if (newColumn.field === 'name') {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column)]) {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return { columns: fullSetColumns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm };
};
